import { isObject } from 'lodash';
import { isObservableArray, set } from 'mobx';

export function isArray<T>(value: T | readonly T[]): value is T[] {
    return Array.isArray(value);
}

export function isArrayLike(value: any): value is any[] {
    return Array.isArray(value) || isObservableArray(value);
}

export function isNotArray<T>(value: T | readonly T[]): value is T {
    return !Array.isArray(value);
}

export function toArray<T, U>(map: Map<U, T>) {
    return Array.from(map, ([k, v]) => v);
}

export function toKeyArray<T, U>(map: Map<U, T>) {
    return Array.from(map, ([k, v]) => k);
}

export function toMap<K, V>(array: V[] | undefined, keySelector: (entry: V) => K): Map<K, V> {
    if (array == undefined) return new Map<K, V>();

    const kv = array.map(e => [keySelector(e), e] as [K, V]);
    return new Map(kv);
}

export function addToMap<K, V>(
    map: Map<K, V>,
    array: V[] | undefined,
    keySelector: (entry: V) => K,
    onCreate: (entry: V) => V = defaultOnCreate,
    onUpdate: (entry: V, update: V) => void = defaultOnUpdate
) {
    if (array == undefined) return;

    for (const v of array) {
        const k = keySelector(v);
        const e = map.get(k);

        if (e && isObject(e)) {
            onUpdate(e, v);
        } else {
            map.set(k, onCreate(v));
        }
    }
}

export function setMap<K, V>(
    map: Map<K, V>,
    array: V[] | undefined,
    keySelector: (entry: V) => K,
    onCreate: (entry: V) => V = defaultOnCreate,
    onUpdate: (entry: V, update: V) => void = defaultOnUpdate
) {
    if (array == undefined) return;

    const m = toMap(array, keySelector);

    for (const [k, v] of m) {
        const e = map.get(k);

        if (e && isObject(e)) {
            onUpdate(e, v);
        } else {
            map.set(k, onCreate(v));
        }
    }

    for (const k of map.keys()) if (!m.has(k)) map.delete(k);
}

export function findByIterator<T>(iterator: IterableIterator<T>, predicate: (value: T) => boolean) {
    while (true) {
        const { value, done } = iterator.next();
        if (done) return;
        else if (predicate(value)) return value;
    }
}

function defaultOnCreate<V>(entry: V) {
    return entry;
}

function defaultOnUpdate<V>(entry: V, update: V) {
    return set(entry, update);
}

export function undefinedIfEmpty<T>(value: T[] | null | undefined) {
    if (!value) return;
    if (value.length == 0) return;

    return value;
}

export function arrayMove<T>(array: T[], fromIndex: number, toIndex: number) {
    const element = array[fromIndex];
    array.splice(fromIndex, 1);
    array.splice(toIndex, 0, element);

    return array;
}
